import type { Properties, Units } from '@turf/turf';
import type { Layer, PaddingOptions } from 'core/utils/map/mapbox-gl';
import { MapboxStyle } from './core-map.constants';

/**
 * Map tiles are stored in a quadtree data structure. At zoom level 0, you can see a map of the whole Earth,
 * and this image is contained in a single tile. At zoom level 1, the single tile you saw at zoom level 0 splits into
 * exactly four tiles so the whole world fits in a 2x2 tile square. Each zoom level quadtree divides the tiles of the
 * one before it, which creates a grid of 2^zoom x 2^zoom. The highest zoom level, 22, is a 2^22 x 2^22 grid.
 */
enum MapboxZoomLevel {
  EARTH_LEVEL = 0,
  CONTINENT_LEVEL = 3,
  LARGE_ISLAND_LEVEL = 4,
  LARGE_RIVERS_LEVEL = 6,
  BIG_REGION_LEVEL = 8,
  LARGE_ROADS_LEVEL = 10,
  REGION_LEVEL = 12,
  FIELD_LEVEL = 14,
  BUILDINGS_LEVEL = 16,
  ICON_LEVEL = 19
}

export enum MapboxLayer {
  BACKGROUND = 'background',
  AREAS_HOVER = 'areas-hover',
  AREAS_FILL = 'areas-fill',
  AREAS_SELECTED = 'areas-selected',
  AREAS_BOUNDARY = 'areas-boundary',
  AREAS_REPRESENTATIVENESS = 'areas-representativiness'
}

export enum MapboxTypeLayer {
  SYMBOL = 'symbol',
  FILL = 'fill',
  LINE = 'line',
  CIRCLE = 'circle',
  FILL_EXTRUSION = 'fill-extrusion',
  RASTER = 'raster',
  BACKGROUND = 'background',
  HEATMAP = 'heatmap',
  HILLSHADE = 'hillshade'
}

export const MapboxConfigurationDefaultOptions = {
  EMPTY: {},
  DEFAULT_PADDING: { padding: { left: 300, top: 30, bottom: 30, right: 30 } },
  ZOOM_LEVEL_REGION: { zoom: MapboxZoomLevel.REGION_LEVEL },
  ZOOM_LEVEL_FIELD: { zoom: MapboxZoomLevel.BUILDINGS_LEVEL }
};

export interface MapBoxFitBoundsOptions {
  padding?: PaddingOptions;
  zoom?: MapboxZoomLevel;
}

export enum MapboxFilterOption {
  CASE = 'case',
  IN = 'in',
  COALESCE = 'coalesce',
  BOOLEAN = 'boolean'
}

export enum MapboxConditionProperty {
  HAS = 'has',
  GET = 'get',
  LITERAL = 'literal'
}

export enum MapboxProperty {
  ID = 'id',
  PARENT_ID = 'parent_id',
  ICON_OFFSET = 'icon_offset',
  POINT_COUNT = 'point_count',
  ICON = 'icon',
  FEATURE_STATE = 'feature-state'
}

export enum MapboxSource {
  POINTS = 'points',
  GRID_POLYGON = 'grid-polygon',
  AREAS_LABEL = 'areas-label',
  AREAS_SOURCE = 'areas-source',
  DISEASES_NO_DATA = 'diseases-no-data'
}

export enum MapboxLayoutLineColor {
  BLUE_1 = '#0071CD',
  BLUE_0 = '#61d8ed'
}

export enum MapboxBackgroundColor {
  DARK_MODE = '#232630',
  DEFAULT = 'hsl(47, 26%, 88%)'
}

export const MapboxTile = Object.freeze({
  TRANSPARENT: `https://access.maxar.com/earthservice/tmsaccess/tms/1.0.0/Digitalglobe:OSMTransparentTMSTileService@EPSG:3857@jpg/{z}/{x}/{y}.jpg?connectId=f9695616-1112-48f0-8c71-5293ae799786&host=${window.location.hostname}`,
  SATELLITE: `https://access.maxar.com/earthservice/tmsaccess/tms/1.0.0/DigitalGlobe:ImageryTileService@EPSG:3857@jpg/{z}/{x}/{y}.jpg?connectId=f9695616-1112-48f0-8c71-5293ae799786&host=${window.location.hostname}`,
  DARK: MapboxStyle.DARK,
  SATELLITE_MAPBOX: MapboxStyle.SATELLITE,
  SATELLITE_STREETS: MapboxStyle.SATELLITE_STREETS,
  LIGHT_MODE: MapboxStyle.LIGHT
});

export const MapboxLayerInstance = {
  DARK_MODE: {
    id: MapboxLayer.BACKGROUND,
    type: MapboxLayer.BACKGROUND,
    paint: { 'background-color': MapboxBackgroundColor.DARK_MODE }
  } as Layer,
  SATELLITE: {
    id: MapboxLayer.BACKGROUND,
    type: MapboxLayer.BACKGROUND,
    paint: { 'background-color': MapboxBackgroundColor.DEFAULT }
  } as Layer
};

export interface DrawPolygonFromPointProps {
  lngLat: GeoJSON.Position;
  xSemiAxis: number;
  ySemiAxis: number;
  options?: {
    steps?: number;
    units?: Units;
    properties?: Properties;
  };
  flagRemoveRevertCoordinates?: boolean | string;
}
